import React, { useEffect, useRef } from 'react';
import Router from 'next/router';
import { useConfirm } from './ConfirmProvider';
import { setUnsaved, getUnsavedLabels, unsavedLeaveConfirmOptions, type UnsavedScope } from '../../lib/unsavedChanges';

/**
 * 未保存の変更の警告（2026-10-05・UX 監査 A-4）。登録簿は src/lib/unsavedChanges.ts。
 *
 * - useUnsavedChanges(id, label, dirty): 設定カードが「今、未保存の変更があるか」を登録する（外れたら自動で外す）
 * - UnsavedBadge: カードのタイトルの横に出す「未保存」の印
 * - UnsavedChangesGuard: アプリに 1 つだけ置く見張り。未保存があるときに、別のページへ移る・タブを閉じる・更新するときに確認する
 */

/** scope='store': ヘッダーで店舗を切り替えると消える入力（店舗ごとの設定カード）。既定の 'page' は、ページを離れると消える入力 */
export function useUnsavedChanges(id: string, label: string, dirty: boolean, scope: UnsavedScope = 'page'): void {
  useEffect(() => {
    setUnsaved(id, label, dirty, scope);
  }, [id, label, dirty, scope]);
  // 画面から外れたカードの分は残さない（残すと、もう無いカードのせいで確認が出続ける）
  useEffect(() => () => setUnsaved(id, label, false), [id, label]);
}

export function UnsavedBadge({ dirty }: { dirty: boolean }) {
  if (!dirty) return null;
  return (
    <span
      role="status"
      className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 align-middle text-xs font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
    >
      未保存
    </span>
  );
}

/**
 * 見張りが移動を止めたときに throw するエラー。MainLayout の左メニュー（handleNavigation）は、移動に失敗すると
 * router.replace → window.location.assign（= ブラウザ標準の確認が出る）と順に試すので、`unsavedGuard` を見て何もせず戻る。
 */
export function isUnsavedGuardAbort(error: unknown): boolean {
  return !!error && typeof error === 'object' && (error as { unsavedGuard?: boolean }).unsavedGuard === true;
}

function abortError(): Error {
  return Object.assign(new Error('Route change aborted: unsaved changes'), { unsavedGuard: true, cancelled: true });
}

export function UnsavedChangesGuard(): null {
  const confirm = useConfirm();
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;
  // 「保存せずに移動する」を選んだあとの、こちらから起こした移動は止めない
  const bypass = useRef(false);
  // 確認を開いている間に、もう 1 回移動が起きても確認を重ねない
  const asking = useRef(false);

  useEffect(() => {
    // 確認を出し、「保存せずに移動する」ならその URL へ移る。
    // ⚠️ 登録簿はここで空にしない。移動が成り立たなかったときに「未保存の印は出たまま、以後は無警告」になるため。
    //    移動できれば、カードが外れる（unmount）ときに自分で外れる。bypass は、こちらから起こした移動だけを通す
    const askAndGo = (url: string) => {
      if (asking.current) return;
      asking.current = true;
      confirmRef.current(unsavedLeaveConfirmOptions(getUnsavedLabels())).then((ok) => {
        asking.current = false;
        if (!ok) return;
        bypass.current = true;
        Router.push(url)
          .catch(() => {})
          .finally(() => {
            bypass.current = false;
          });
      });
    };

    // タブを閉じる・更新・アドレスを直接変える（ブラウザ標準の確認。文言は変えられない）
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (bypass.current || getUnsavedLabels().length === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    // アプリ内の移動（リンク・router.push）。クエリだけの shallow な変更（?open=… の掃除など）は対象にしない
    const onRouteChangeStart = (url: string, opts?: { shallow?: boolean }) => {
      if (bypass.current || opts?.shallow) return;
      if (getUnsavedLabels().length === 0) return;
      // いったん移動を止め（Next の流儀: routeChangeError を出して throw）、確認の結果で改めて移動する
      Router.events.emit('routeChangeError');
      askAndGo(url);
      throw abortError();
    };
    Router.events.on('routeChangeStart', onRouteChangeStart);

    // ブラウザの「戻る」「進む」（iPad のスワイプ戻りを含む）。このときは URL が先に変わってから Next に知らされるので、
    // 止めるだけだと「画面は設定ページ・URL は前のページ」とずれる。今のページの URL を積み直してから確認する
    Router.beforePopState(({ as }) => {
      if (bypass.current || getUnsavedLabels().length === 0) return true;
      try {
        window.history.pushState(null, '', Router.asPath);
      } catch {
        // 積み直せなくても、確認は出す
      }
      askAndGo(as);
      return false;
    });

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      Router.events.off('routeChangeStart', onRouteChangeStart);
      Router.beforePopState(() => true);
    };
  }, []);

  return null;
}
